import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import {
  Joyride,
  EVENTS,
  STATUS,
  type EventData,
  type ButtonType,
} from 'react-joyride';
import { useTranslation } from 'react-i18next';
import { buildTourSteps } from './buildSteps';
import { markTourCompleted, markTourSkipped } from './storage';
import { joyrideOptions, joyrideStyles, tourFloatingOptions } from './styles';
import { isTourRouteMatch } from './tourRoutes';
import './joyride.css';
import type { TourId } from './types';

type PausedTour = {
  tourId: TourId;
  stepIndex: number;
};

type TourContextValue = {
  startTour: (tourId: TourId) => void;
  pauseTour: () => void;
  stopTour: () => void;
  isRunning: boolean;
  activeTourId: TourId | null;
  isPausedForTour: (tourId: TourId) => boolean;
};

const TourContext = createContext<TourContextValue>({
  startTour: () => {},
  pauseTour: () => {},
  stopTour: () => {},
  isRunning: false,
  activeTourId: null,
  isPausedForTour: () => false,
});

type TourProviderProps = {
  children: ReactNode;
  userId?: string;
};

export const TourProvider = ({ children, userId }: TourProviderProps) => {
  const { t } = useTranslation('joyride');
  const location = useLocation();
  const [activeTourId, setActiveTourId] = useState<TourId | null>(null);
  const [run, setRun] = useState(false);
  const [resumeStepIndex, setResumeStepIndex] = useState(0);
  const pausedTourRef = useRef<PausedTour | null>(null);
  const currentStepIndexRef = useRef(0);

  const steps = useMemo(
    () => (activeTourId ? buildTourSteps(activeTourId, t) : []),
    [activeTourId, t],
  );

  const locale = useMemo(
    () => ({
      back: t('buttons.back'),
      close: t('buttons.close'),
      last: t('buttons.finish'),
      next: t('buttons.next'),
      nextWithProgress: t('buttons.nextWithProgress'),
      skip: t('buttons.skip'),
    }),
    [t],
  );

  const stopTour = useCallback(() => {
    pausedTourRef.current = null;
    setRun(false);
    setActiveTourId(null);
    setResumeStepIndex(0);
  }, []);

  const pauseTour = useCallback(() => {
    if (!activeTourId || !run) {
      return;
    }

    pausedTourRef.current = {
      tourId: activeTourId,
      stepIndex: currentStepIndexRef.current,
    };
    setRun(false);
  }, [activeTourId, run]);

  const startTour = useCallback(
    (tourId: TourId) => {
      let stepIndex = 0;

      if (pausedTourRef.current?.tourId === tourId) {
        stepIndex = pausedTourRef.current.stepIndex;
        pausedTourRef.current = null;
      }

      setResumeStepIndex(stepIndex);

      if (activeTourId === tourId) {
        setRun(true);
        return;
      }

      setActiveTourId(tourId);
      setRun(true);
    },
    [activeTourId],
  );

  const isPausedForTour = useCallback(
    (tourId: TourId) => pausedTourRef.current?.tourId === tourId,
    [],
  );

  useEffect(() => {
    if (!run || !activeTourId) {
      return;
    }

    if (isTourRouteMatch(activeTourId, location.pathname)) {
      return;
    }

    pausedTourRef.current = {
      tourId: activeTourId,
      stepIndex: currentStepIndexRef.current,
    };
    setRun(false);
  }, [activeTourId, location.pathname, run]);

  const handleEvent = useCallback(
    (data: EventData) => {
      const { type, status } = data;

      currentStepIndexRef.current = data.index;

      if (type === EVENTS.TOUR_END && activeTourId) {
        if (status === STATUS.FINISHED) {
          markTourCompleted(userId, activeTourId);
          pausedTourRef.current = null;
        } else if (status === STATUS.SKIPPED) {
          markTourSkipped(userId, activeTourId);
          pausedTourRef.current = null;
        }
        stopTour();
      }
    },
    [activeTourId, stopTour, userId],
  );

  const value = useMemo(
    () => ({
      startTour,
      pauseTour,
      stopTour,
      isRunning: run,
      activeTourId,
      isPausedForTour,
    }),
    [activeTourId, isPausedForTour, pauseTour, run, startTour, stopTour],
  );

  const mergedOptions = useMemo(
    () => ({
      ...joyrideOptions,
      buttons: ['back', 'skip', 'primary'] as ButtonType[],
      skipBeacon: false,
    }),
    [],
  );

  return (
    <TourContext.Provider value={value}>
      {children}
      {activeTourId && (
        <Joyride
          run={run}
          steps={steps}
          continuous
          initialStepIndex={resumeStepIndex}
          locale={locale}
          styles={joyrideStyles}
          options={mergedOptions}
          floatingOptions={tourFloatingOptions}
          onEvent={handleEvent}
        />
      )}
    </TourContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components -- context hook co-located with provider
export const useTourContext = () => useContext(TourContext);
